import {
    ArticleType,
} from '@seek/cmsu-cms-connect';
import {
    CategoryLink,
    PodcastBanner,
    PodcastPlayer,
    SocialMediaShare,
    Video
} from '../';
import {
    articleActionByType,
    getHygraphLivePreviewProps,
    prefixUrlWithLanguage
} from '../../helpers/';
import { getArticleFeatureImage } from '../../helpers/articleHelper';
import { useCMSUContext, useTargetBlank } from '../../hooks/';
import translationsArticle from '../../modules/ArticleItemFooter/.vocab';
import { resizeImageFromHygraph } from '../../utils/imageHelper';
import { RichText } from '@seek/cmsu-rich-text';
import { BlockQuote } from '@seek/cmsu-rich-text/src/components/BlockQuote/BlockQuote';
import { useTranslations } from '@vocab/react';
import {
    Box,
    ContentBlock,
    Divider,
    Heading,
    Hidden,
    Inline,
    Stack,
    Text,
    TextLink,
} from 'braid-design-system';

import type { ArticleAdditionalData, ArticleLocalization } from './types';
import { richTextRenderer } from '../../renderer';

import translations from '../../.vocab'
import type { LocalesInfo } from '../types';

// The Hygraph site that the article should be fetched from
type ArticleComponentProps = {
    articleData: ArticleLocalization
    & LocalesInfo
    & { additionalData: ArticleAdditionalData };
};

export const Article = ({ articleData }: ArticleComponentProps) => {
    useTargetBlank();
    const { t } = useTranslations(translations);
    const { t: t_article } = useTranslations(translationsArticle);

    const {
        fullURL,
        sectionName,
    } = useCMSUContext();

    // // Get the article content by using the loader.
    // const data = useLoaderData() as ArticleLocalization &
    //     LocalesInfo & { additionalData: ArticleAdditionalData };

    const {
        id,
        category: articleCategory,
        content,
        title,
        articleType,
        authors,
        mediaUrl,
        podcast,
        seo,
        timeToRead,
        locale,
        additionalData,
        excerpt,
        showExcerpt,
        stage,
    } = articleData;


    // Get the feature image data from either Bynder or Hygraph sources
    const articleFeatureImage = getArticleFeatureImage(articleData);
    const { imageUrl, height, width, imageAltText } = articleFeatureImage;

    let imageUrlFormatted = imageUrl;
    if (height && width) {
        const resizeHieght = (height * 940) / width;
        const fit = resizeHieght > 485 ? 'crop' : 'clip';
        imageUrlFormatted = resizeImageFromHygraph({
            imageUrl,
            height: 485,
            width: 940,
            fit,
        });
    }

    const categoryName = articleCategory?.name || '';
    const categorySlug = articleCategory?.slug || '';
    const rawContent = content.raw;
    const embedReferences = content.references;
    const description = seo?.description || title;
    const parentSection =
        articleCategory?.relatedCategorySection?.parentSection?.sectionName;
    const relatedSectionName =
        articleCategory?.relatedCategorySection?.sectionName;

    const articleAction = articleActionByType(articleType);
    const isPodcastArticleType = articleType?.toLowerCase() === 'podcast';

    const isShowVideo = articleType === ArticleType.Video && Boolean(mediaUrl);

    const displayExcerpt = Boolean(excerpt && showExcerpt);

    const hygraphLivePreviewProps = getHygraphLivePreviewProps(id, locale, stage);

    return (

        <Box background="surface" component="article">
            <Stack space="gutter">
                {isShowVideo && <Video url={mediaUrl || ''} />}
                {!isShowVideo && Boolean(imageUrl) && (
                    <Box
                        background="surface"
                        borderRadius="large"
                        overflow="hidden"
                    >
                        <Box
                            component="img"
                            src={imageUrlFormatted}
                            alt={imageAltText || title}
                            style={{
                                display: 'block',
                                width: '100%',
                            }}
                        />
                    </Box>
                )}
                <ContentBlock width="small">
                    <Stack space="gutter">
                        <Inline
                            space={{
                                mobile: 'small',
                                tablet: 'medium',
                            }}
                            alignY="center"
                        >
                            <CategoryLink
                                name={categoryName}
                                slug={categorySlug}
                                basePath={
                                    parentSection
                                        ? `${sectionName}/${relatedSectionName}`
                                        : sectionName
                                }
                                locale={locale}
                            />
                            <Inline space="xxsmall" alignY="center">
                                {Boolean(timeToRead) && (
                                    <Text tone="secondary">{`${timeToRead} ${t_article(
                                        articleAction,
                                    )}`}</Text>
                                )}
                                {authors && authors.length > 0 && (
                                    <Text tone="secondary">
                                        {Boolean(timeToRead) && <>&#183;&nbsp;</>}
                                        {t('Written by')}{' '}
                                        {authors.map((author, index) => (
                                            <span key={author.name}>
                                                {
                                                    <TextLink
                                                        href={authorLink(author.slug, locale)}
                                                        key={author.name}
                                                    >
                                                        {author.name}
                                                    </TextLink>
                                                }
                                                {index < authors.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                    </Text>
                                )}
                            </Inline>
                        </Inline>

                        <Heading
                            level="2"
                            component="h1"
                            data={{
                                ...hygraphLivePreviewProps,
                                'field-api-id': 'title',
                            }}
                        >
                            {title}
                        </Heading>

                        <Box paddingBottom="medium">
                            <SocialMediaShare
                                pageUrl={fullURL || ''}
                                title={title}
                                summary={description}
                            />
                        </Box>
                        {isPodcastArticleType && (
                            <PodcastPlayer
                                mediaUrl={mediaUrl}
                                data={{
                                    ...hygraphLivePreviewProps,
                                }}
                            />
                        )}
                        {displayExcerpt && (
                            <BlockQuote
                                data={{
                                    ...hygraphLivePreviewProps,
                                }}
                            >
                                {excerpt}
                            </BlockQuote>
                        )}
                        <RichText
                            content={rawContent}
                            renderers={richTextRenderer(additionalData)}
                            references={embedReferences}
                            data={{
                                ...hygraphLivePreviewProps,
                            }}
                        />
                        {isPodcastArticleType && (
                            <PodcastBanner
                                podcast={podcast}
                                data={{ ...hygraphLivePreviewProps }}
                            />
                        )}

                        <Hidden below="tablet">
                            <Divider weight="regular" />
                        </Hidden>

                        {/* Only display the trailing SocialMediaShare component if the Article
                            content is greater than 3 Pragraph (or similar) elements  */}
                        {rawContent.children.length > 3 && (
                            <SocialMediaShare
                                pageUrl={fullURL || ''}
                                title={title}
                                summary={description}
                            />
                        )}
                    </Stack>
                </ContentBlock>
            </Stack>
        </Box>

    );
};

export const authorLink = (slug: string, locale: string) =>
    prefixUrlWithLanguage({
        url: `/author/${slug}`,
        locale,
    });